import './ChopLinesExtended.scss';

import debounce from 'lodash.debounce';
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

const Wrapper = ({ forwardedRef, lineHeight, maxHeight, overflow, children, ...rest }) => (
    <div
        {...rest}
        style={{
            position: 'relative',
            lineHeight,
            maxHeight,
            overflow,
        }}
        ref={forwardedRef}
    >
        {children}
    </div>
);

const AutoSizer = ({ children, forwardedRef }) => (
    <div
        style={{
            pointerEvents: 'none',
            display: 'block',
            width: '100%',
        }}
        ref={forwardedRef}
    >
        {children}
    </div>
);

const Ellipsis = ({ children, rightOffset = 0 }) => (
    <div
        className="chopline-ellipsis"
        style={{
            right: rightOffset,
        }}
    >
        {children}
    </div>
);

const ChopLinesExtended = ({
    // use lines and lineHeight together or use maxHeight alone
    lines = 0,
    lineHeight = 0,
    maxHeight = 0,
    ellipsis = '…',
    children,
    overflowChange,
    disabled,
    ...rest
}): ReactElement => {
    const autoSizerRef = React.createRef();
    const wrapperRef = React.createRef();
    const [measuredHeight, setMeasuredHeight] = useState(0);
    const maxHeightPx = `${maxHeight || lineHeight * lines}px`;
    const doesOverflow = useMemo(
        () => measuredHeight > (maxHeight || lineHeight * lines),
        [lineHeight, lines, maxHeight, measuredHeight],
    );
    const [ellipsisRightOffset, setEllipsisRightOffset] = useState(0);

    const measureAutoSizer = useCallback(() => {
        if (!autoSizerRef?.current) {
            return;
        }
        const { height } = autoSizerRef.current.getBoundingClientRect();
        setMeasuredHeight(height);
        if (overflowChange) {
            overflowChange(height > (maxHeight || lineHeight * lines));
        }
    }, [autoSizerRef, lineHeight, maxHeight, lines, overflowChange]);

    useEffect(() => {
        const handleResize: any = debounce(() => {
            window.requestAnimationFrame(measureAutoSizer);
        }, 50);

        measureAutoSizer();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Calculates ellipsis absolute position
    const getEllipsisPosition = useCallback(() => {
        if (wrapperRef?.current && doesOverflow) {
            const wrapperCoords = wrapperRef?.current?.getBoundingClientRect();
            const nodes = [...wrapperRef?.current?.querySelectorAll('[datatype]')] || [];
            const isOneChildOnly = nodes.length === 1;
            // FFiltering to get the child elements that are in the scope of the wrapper
            const visibleNodes = isOneChildOnly
                ? nodes
                : nodes.filter((node) => {
                      const nodeCoords = node.getBoundingClientRect();
                      return (
                          nodeCoords.top < wrapperCoords.bottom &&
                          nodeCoords.bottom - 40 <= wrapperCoords.bottom &&
                          nodeCoords.right <= wrapperCoords.right
                      );
                  });

            const lastChild = isOneChildOnly ? visibleNodes[0] : visibleNodes[visibleNodes.length - 1];
            lastChild?.classList?.add('clear-after');
            // getting last child due we need to add ellipse to the last visible word
            const lastChildCoords = lastChild?.getBoundingClientRect();
            // if child width is too big, then the ellipsis abolute right position will be the difference between the width of the wrapper and the width of the child element
            // else will be the difference between the right coords of the wrapper and child
            const offset =
                lastChildCoords?.width > 200
                    ? wrapperRef?.current?.offsetWidth - lastChildCoords?.width
                    : wrapperCoords?.right - lastChildCoords?.right;

            // (- 100) <= taking away the ellipsis with to avoid overlaying the last letters
            setEllipsisRightOffset(offset - 100);
        }
    }, [doesOverflow, wrapperRef]);

    useEffect(() => {
        getEllipsisPosition();
        window.addEventListener('resize', getEllipsisPosition);
        return () => window.removeEventListener('resize', getEllipsisPosition);
    }, [getEllipsisPosition]);

    if (disabled) {
        return children;
    }

    return (
        <Wrapper
            {...rest}
            forwardedRef={wrapperRef}
            lineHeight={maxHeight ? 'unset' : `${lineHeight}px`}
            maxHeight={!measuredHeight || doesOverflow ? maxHeightPx : 'unset'}
            overflow={!measuredHeight || doesOverflow ? 'hidden' : 'unset'}
        >
            {!doesOverflow && (
                <AutoSizer forwardedRef={autoSizerRef} aria-hidden>
                    {children}
                </AutoSizer>
            )}
            {doesOverflow && children}
            {doesOverflow && <Ellipsis rightOffset={ellipsisRightOffset}>{ellipsis}</Ellipsis>}
        </Wrapper>
    );
};

export default ChopLinesExtended;
